/**
 * CustomerOrdersList Component
 * List of customer orders with pagination and loading states
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-007
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { OrderCard } from './OrderCard';
import { Package, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import type { CustomerOrdersResponse } from '../types/order.types';

// ============================================
// COMPONENT PROPS
// ============================================

export interface CustomerOrdersListProps {
  data?: CustomerOrdersResponse;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onViewDetails: (orderId: number) => void;
  onPageChange: (page: number) => void;
  currentPage: number;
}

// ============================================
// LOADING SKELETON
// ============================================

function OrderLoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-6 w-20" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
            <Skeleton className="h-9 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ============================================
// EMPTY STATE
// ============================================

function EmptyState() {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-slate-100 p-4 mb-4">
          <Package className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          No orders found
        </h3>
        <p className="text-sm text-slate-500 max-w-sm">
          You haven't placed any orders yet. Your order history will appear here.
        </p>
      </CardContent>
    </Card>
  );
}

// ============================================
// ERROR STATE
// ============================================

function ErrorState({ error }: { error: Error | null }) {
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>
        {error?.message || 'Failed to load orders. Please try again.'}
      </AlertDescription>
    </Alert>
  );
}

// ============================================
// PAGINATION CONTROLS
// ============================================

function PaginationControls({
  currentPage,
  totalPages,
  hasNext,
  hasPrevious,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between mt-6">
      <p className="text-sm text-slate-600">
        Page {currentPage + 1} of {totalPages}
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!hasPrevious}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNext}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * CustomerOrdersList Component
 * Displays paginated list of customer orders with loading and error states
 *
 * @example
 * ```tsx
 * <CustomerOrdersList
 *   data={ordersData}
 *   isLoading={isLoading}
 *   isError={isError}
 *   error={error}
 *   onViewDetails={(id) => navigate(`/orders/${id}`)}
 *   onPageChange={setPage}
 *   currentPage={page}
 * />
 * ```
 */
export function CustomerOrdersList({
  data,
  isLoading,
  isError,
  error,
  onViewDetails,
  onPageChange,
  currentPage,
}: CustomerOrdersListProps) {
  // Loading state
  if (isLoading) {
    return <OrderLoadingSkeleton />;
  }

  // Error state
  if (isError) {
    return <ErrorState error={error} />;
  }

  // Empty state
  if (!data || data.orders.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-4">
      {/* Order Cards */}
      <div className="space-y-4">
        {data.orders.map((order) => (
          <OrderCard
            key={order.id}
            order={order}
            onViewDetails={onViewDetails}
          />
        ))}
      </div>

      {/* Pagination */}
      {data.totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={data.totalPages}
          hasNext={data.hasNext}
          hasPrevious={data.hasPrevious}
          onPageChange={onPageChange}
        />
      )}

      {/* Summary */}
      <p className="text-sm text-slate-500 text-center mt-4">
        Showing {data.orders.length} of {data.totalElements} orders
      </p>
    </div>
  );
}

export default CustomerOrdersList;
